/** Explicit owner-only tennis club membership deduplication UI. */
export const CLUB_MEMBERSHIP_MERGE_OVERLAY: Record<string, { clubMembershipMerge: Record<string, string> }> = {
  en: { clubMembershipMerge: {
    title: "Review duplicate club memberships",
    intro: "These clubs have similar names in the same city. This does not prove they are the same organization.",
    keep: "Keep this membership",
    remove: "Remove the other membership",
    safety: "Only your club memberships change. Global club directory, other users, historical matches and Club Elo data remain unchanged.",
    primary: "If the removed club was your primary club, the one you keep becomes primary. Discoverability stays as already set on the club you keep.",
    confirm: "I confirm I want to remove one of my memberships. I understand this does NOT merge the clubs for other players.",
    submit: "Consolidate my memberships",
    submitting: "Updating memberships…",
    success: "Duplicate membership removed.",
    failure: "Could not update. Refresh the list and review both clubs.",
    cancel: "Cancel",
  } },
  el: { clubMembershipMerge: {
    title: "Έλεγχος διπλών συμμετοχών σε ομίλους",
    intro: "Οι όμιλοι έχουν παρόμοιο όνομα στην ίδια πόλη. Αυτό δεν αποδεικνύει ότι πρόκειται για τον ίδιο οργανισμό.",
    keep: "Διατήρηση αυτής της συμμετοχής",
    remove: "Αφαίρεση της άλλης συμμετοχής",
    safety: "Αλλάζουν μόνο οι προσωπικές σου συμμετοχές. Ο παγκόσμιος κατάλογος, οι άλλοι χρήστες, οι παλιοί αγώνες και τα δεδομένα Club Elo μένουν ανέπαφα.",
    primary: "Αν αφαιρεθεί ο κύριος όμιλος, αυτός που κρατάς γίνεται κύριος. Η επιλογή προβολής σε άλλους παραμένει όπως την είχες ορίσει στον όμιλο που κρατάς.",
    confirm: "Επιβεβαιώνω ότι θέλω να αφαιρέσω μία προσωπική συμμετοχή. Καταλαβαίνω ότι ΔΕΝ συγχωνεύονται οι όμιλοι για τους άλλους παίκτες.",
    submit: "Ενοποίηση συμμετοχών μου",
    submitting: "Ενημέρωση συμμετοχών…",
    success: "Αφαιρέθηκε η διπλή συμμετοχή.",
    failure: "Η αλλαγή δεν ολοκληρώθηκε. Ανανέωσε τη λίστα και έλεγξε τους δύο ομίλους.",
    cancel: "Ακύρωση",
  } },
  es: { clubMembershipMerge: {
    title: "Revisar afiliaciones duplicadas",
    intro: "Los clubes tienen nombres parecidos en la misma ciudad. Esto no demuestra que sean la misma entidad.",
    keep: "Conservar esta afiliación",
    remove: "Quitar la otra afiliación",
    safety: "Solo cambian tus afiliaciones. No se modifica el directorio mundial, otros usuarios, partidos antiguos ni datos Elo.",
    primary: "Si quitas tu club principal, el conservado pasa a ser el principal. La visibilidad del conservado no cambia.",
    confirm: "Confirmo que quiero quitar una afiliación. Entiendo que NO fusiona los clubes para otros jugadores.",
    submit: "Unificar mis afiliaciones",
    submitting: "Actualizando…",
    success: "Se eliminó la afiliación duplicada.",
    failure: "No se pudo actualizar. Recarga y revisa los clubes.",
    cancel: "Cancelar",
  } },
  fr: { clubMembershipMerge: {
    title: "Vérifier les adhésions en double",
    intro: "Ces clubs portent des noms proches dans la même ville. Cela ne prouve pas qu'ils sont identiques.",
    keep: "Conserver cette adhésion",
    remove: "Supprimer l'autre adhésion",
    safety: "Seules vos adhésions changent. L'annuaire mondial, les autres joueurs, les anciens matchs et l'Elo restent inchangés.",
    primary: "Si le club supprimé était principal, le club conservé le devient. Sa visibilité ne change pas.",
    confirm: "Je confirme vouloir supprimer une adhésion. Cela ne fusionne PAS les clubs pour les autres joueurs.",
    submit: "Regrouper mes adhésions",
    submitting: "Mise à jour…",
    success: "Adhésion en double supprimée.",
    failure: "Mise à jour impossible. Actualisez la liste.",
    cancel: "Annuler",
  } },
  de: { clubMembershipMerge: {
    title: "Doppelte Vereinsmitgliedschaften prüfen",
    intro: "Diese Vereine haben ähnliche Namen in derselben Stadt. Das beweist nicht, dass sie identisch sind.",
    keep: "Diese Mitgliedschaft behalten",
    remove: "Andere Mitgliedschaft entfernen",
    safety: "Nur deine Mitgliedschaften ändern sich. Verzeichnis, andere Spieler, alte Matches und Club-Elo bleiben unverändert.",
    primary: "War der entfernte Verein dein Hauptverein, wird der behaltene zum Hauptverein. Seine Sichtbarkeit bleibt unverändert.",
    confirm: "Ich bestätige, dass ich eine Mitgliedschaft entfernen möchte. Die Vereine werden NICHT für andere Spieler zusammengeführt.",
    submit: "Mitgliedschaften bereinigen",
    submitting: "Mitgliedschaften werden aktualisiert…",
    success: "Doppelte Mitgliedschaft entfernt.",
    failure: "Änderung fehlgeschlagen. Lade die Liste neu und prüfe die Vereine.",
    cancel: "Abbrechen",
  } },
  it: { clubMembershipMerge: {
    title: "Controlla le iscrizioni duplicate",
    intro: "I club hanno nomi simili nella stessa città. Questo non dimostra che siano la stessa società.",
    keep: "Mantieni questa iscrizione",
    remove: "Rimuovi l'altra iscrizione",
    safety: "Cambiano solo le tue iscrizioni. Elenco globale, altri utenti, partite storiche ed Elo rimangono invariati.",
    primary: "Se rimuovi il club principale, quello mantenuto diventa principale. La visibilità rimane invariata.",
    confirm: "Confermo di voler rimuovere una mia iscrizione. Questo NON unisce i club per gli altri giocatori.",
    submit: "Consolida le mie iscrizioni",
    submitting: "Aggiornamento…",
    success: "Iscrizione duplicata rimossa.",
    failure: "Aggiornamento non riuscito. Ricarica l'elenco e controlla i club.",
    cancel: "Annulla",
  } },
  ja: { clubMembershipMerge: {
    title: "重複するクラブ登録を確認",
    intro: "同じ都市に似た名前のクラブがあります。同一のクラブとは限りません。",
    keep: "この登録を残す",
    remove: "もう一方の登録を削除",
    safety: "変更されるのは自分のクラブ登録のみです。世界共通のクラブ名簿、他の利用者、過去の試合、Club Elo は変更されません。",
    primary: "削除側がメインクラブだった場合、残した登録をメインにします。公開設定は変更されません。",
    confirm: "自分の登録を1件削除することに同意します。他の選手のクラブを統合する操作ではないと理解しています。",
    submit: "登録を整理する",
    submitting: "更新中…",
    success: "重複登録を削除しました。",
    failure: "更新できませんでした。リストを更新して再確認してください。",
    cancel: "キャンセル",
  } },
};
